// Fades elements in as they scroll into view:
//   - top-level sections (#MainContent > .shopify-section)
//   - product cards in collection / search grids (.product-grid__item)
//
// The initial hidden state is applied by CSS via the `data-scroll-reveal`
// attribute on <html> (set in theme.liquid, so it applies before first paint —
// no flash). This script reveals each element as it intersects the viewport.
//
// Grid cards are also added dynamically (infinite scroll appends/prepends to the
// grid, filters morph the results), so a MutationObserver re-runs the scan to
// pick up any newly added, still-hidden cards. Re-observing is idempotent: the
// scan skips anything already revealed, and observing an element twice is a no-op.
//
// If the effect can't / shouldn't run (no IntersectionObserver, reduced motion,
// or the theme editor), we remove the attribute so everything shows normally.

const root = document.documentElement;

const canReveal =
  'IntersectionObserver' in window &&
  window.matchMedia('(prefers-reduced-motion: no-preference)').matches &&
  !window.Shopify?.designMode;

const SELECTOR = '#MainContent > .shopify-section, .product-grid__item';

if (!canReveal) {
  root.removeAttribute('data-scroll-reveal');
} else {
  const observer = new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('scroll-reveal--in');
        obs.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  const observeAll = () => {
    for (const el of document.querySelectorAll(SELECTOR)) {
      if (!el.classList.contains('scroll-reveal--in')) observer.observe(el);
    }
  };

  const start = () => {
    observeAll();

    // Only product grids add elements dynamically, so skip the mutation watcher
    // entirely on pages without one (product pages, static pages, etc.).
    if (!document.querySelector('.product-grid')) return;

    // Re-scan when the page mutates (infinite scroll, filtering) so newly added
    // cards get revealed. Coalesced to once per frame to handle mutation bursts.
    const main = document.getElementById('MainContent');
    if (main) {
      let scheduled = false;
      new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          observeAll();
        });
      }).observe(main, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}
